import {
  MED_SKU,
  getEligibleMedProducts,
  NON_MED_PLAN,
  HealthPlan,
  isFulldoseGlp1Eligible,
} from "src/utils/medEligibility";
import {
  hasWeightLossGoal,
  hasImproveBodyCompositionGoal,
} from "src/utils/userSegment/features";

function isProductEligible(eligibleProducts: MED_SKU[], sku: MED_SKU): boolean {
  return eligibleProducts.includes(sku);
}

interface RecommendationContext {
  eligibleProducts: MED_SKU[];
  weightLossGoal: boolean;
  improveBodyCompositionGoal: boolean;
}

interface PlanRecommendationRule {
  plan: HealthPlan;
  condition: (context: RecommendationContext) => boolean;
}

function getPlanRecommendationConfig(): PlanRecommendationRule[] {
  /**
   * Priority-ordered configuration for health plan recommendations.
   * First matching rule wins.
   */
  return [
    // improveBodyComposition + full-dose eligible: prefer Microdose when eligible
    {
      plan: MED_SKU.COMPOUNDED_MICRODOSE,
      condition: ({ improveBodyCompositionGoal, eligibleProducts }) =>
        improveBodyCompositionGoal &&
        isFulldoseGlp1Eligible(eligibleProducts) &&
        isProductEligible(eligibleProducts, MED_SKU.COMPOUNDED_MICRODOSE),
    },
    // improveBodyComposition + full-dose eligible: Liraglutide when Microdose not eligible
    {
      plan: MED_SKU.GENERIC_LIRAGLUTIDE,
      condition: ({ improveBodyCompositionGoal, eligibleProducts }) =>
        improveBodyCompositionGoal &&
        isFulldoseGlp1Eligible(eligibleProducts) &&
        isProductEligible(eligibleProducts, MED_SKU.GENERIC_LIRAGLUTIDE),
    },
    // Full-dose GLP-1 with weight loss (priority: Compounded > Liraglutide > Microdose)
    {
      plan: MED_SKU.COMPOUNDED,
      condition: ({ weightLossGoal, eligibleProducts }) =>
        weightLossGoal &&
        isProductEligible(eligibleProducts, MED_SKU.COMPOUNDED),
    },
    {
      plan: MED_SKU.GENERIC_LIRAGLUTIDE,
      condition: ({ weightLossGoal, eligibleProducts }) =>
        weightLossGoal &&
        isProductEligible(eligibleProducts, MED_SKU.GENERIC_LIRAGLUTIDE),
    },
    {
      plan: MED_SKU.COMPOUNDED_MICRODOSE,
      condition: ({ weightLossGoal, eligibleProducts }) =>
        weightLossGoal &&
        isProductEligible(eligibleProducts, MED_SKU.COMPOUNDED_MICRODOSE),
    },

    {
      plan: MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
      condition: ({ eligibleProducts }) =>
        isProductEligible(
          eligibleProducts,
          MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
        ),
    },

    {
      plan: MED_SKU.BRANDED,
      condition: ({ weightLossGoal, eligibleProducts }) =>
        weightLossGoal && isProductEligible(eligibleProducts, MED_SKU.BRANDED),
    },

    {
      plan: MED_SKU.ORALS_METFORMIN,
      condition: ({ weightLossGoal, eligibleProducts }) =>
        weightLossGoal &&
        isProductEligible(eligibleProducts, MED_SKU.ORALS_METFORMIN),
    },

    {
      plan: NON_MED_PLAN.NOOM_WEIGHT,
      condition: ({ weightLossGoal }) => weightLossGoal,
    },
    {
      plan: NON_MED_PLAN.FREEMIUM,
      condition: () => true,
    },
  ];
}

export function getRecommendedHealthPlan(): HealthPlan | undefined {
  const eligibleProducts = getEligibleMedProducts();

  const context: RecommendationContext = {
    eligibleProducts,
    weightLossGoal: hasWeightLossGoal(),
    improveBodyCompositionGoal: hasImproveBodyCompositionGoal(),
  };

  const config = getPlanRecommendationConfig();

  for (const rule of config) {
    if (rule.condition(context)) {
      return rule.plan;
    }
  }

  return undefined;
}
